import React, { useEffect, useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { renderBlock } from "../marketing/emailBlocks";
import { sanitizeHtml } from "../../lib/sanitize";

function PromoCodeBlock({ block }) {
  const [copied, setCopied] = useState(false);
  const appliesLabel = block.appliesTo === "credits" ? "AI Credits Top-Up" : block.appliesTo === "seats" ? "Subscription Seats" : "AI Credits & Subscription Seats";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block.code || "");
    } catch {
      /* clipboard API unavailable (e.g. non-HTTPS) — still show confirmation */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="text-center py-4" data-testid={`popup-promo-${block.id}`}>
      <div className="text-[14px] font-semibold text-slate-900 mb-2.5">{block.label}</div>
      <div className="inline-block border-2 border-dashed border-blue-500 rounded-lg px-6 py-3 text-[22px] font-extrabold tracking-[3px] text-blue-600 bg-blue-50" data-testid="popup-promo-code">
        {block.code}
      </div>
      <div className="mt-2 text-[12.5px] text-slate-500">{block.discountPercent}% off &middot; {appliesLabel}</div>
      <button onClick={copy} data-testid="popup-promo-copy-btn"
        className="mt-3 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold px-4 py-2 rounded-md transition-colors">
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied!" : "Copy Code"}
      </button>
    </div>
  );
}

function CountdownTimerBlock({ block, fallbackTarget }) {
  const targetStr = block.targetAt || fallbackTarget;
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!targetStr) return;
    const target = new Date(targetStr).getTime();
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetStr]);

  if (!targetStr || !remaining) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="text-center py-4" data-testid={`popup-countdown-${block.id}`}>
      <div className="text-[13px] text-slate-500 mb-2">{block.label}</div>
      <div className="text-[26px] font-extrabold text-slate-900 tracking-wider font-mono" data-testid="popup-countdown-value">
        {pad(remaining.d)} : {pad(remaining.h)} : {pad(remaining.m)} : {pad(remaining.s)}
      </div>
      <div className="mt-1 text-[10.5px] text-slate-400 tracking-wide">DAYS &nbsp; HRS &nbsp; MIN &nbsp; SEC</div>
    </div>
  );
}

export default function PopupModal({ popup, onClose }) {
  if (!popup) return null;
  const design = popup.design || {};
  const settings = design.settings || {};

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" data-testid="platform-popup-overlay">
      <div className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
        style={{ backgroundColor: settings.contentBackgroundColor || "#ffffff", fontFamily: settings.fontFamily || "Helvetica, Arial, sans-serif" }}
        data-testid="platform-popup-card">
        <button onClick={onClose} data-testid="platform-popup-close" aria-label="Close"
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center text-slate-700 transition-colors">
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 py-8">
          {(design.rows || []).map((row) => (
            <div key={row.id} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${row.columns.length}, minmax(0,1fr))`, backgroundColor: row.bg || "transparent" }}>
              {row.columns.map((col, ci) => (
                <div key={ci}>
                  {(col || []).map((block) => {
                    if (block.type === "promoCode") return <PromoCodeBlock key={block.id} block={block} />;
                    if (block.type === "countdownTimer") return <CountdownTimerBlock key={block.id} block={block} fallbackTarget={popup.endAt} />;
                    return <div key={block.id} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderBlock(block)) }} />;
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
